import { Field, FormikProps } from "formik";
import { AccordionBody, AccordionHeader, AccordionItem } from "../../Accordion";
import { KlientkontoerklaeringTexts } from "../texts";
import { KlientkontoerklaeringFormValues } from "../../../interfaces/klientkontoerklaering";
import { IsCompleteHelpers } from "../util/isComplete";
import { FormRow, FormRowInput, FormRowLabel } from "../../layouts/FormRow";
import CurrencyField from "../../form/CurrencyField";
import { FormTextGroup } from "../../form/FormTextGroup";
import BigNumber from "bignumber.js";
import { getAfstemning } from "../../../util/klientkontoerklaering";
import IsRequired from "../../form/IsRequired";

const AESection3 = ({
  formikProps,
  isCompleteHelpers,
  texts,
  locked,
}: {
  texts: KlientkontoerklaeringTexts;
  formikProps: FormikProps<KlientkontoerklaeringFormValues>;
  isCompleteHelpers: IsCompleteHelpers;
  locked?: boolean;
}) => {
  const { errors, values, touched } = formikProps;
  const {
    advokatens_section_1_complete,
    advokatens_section_2_complete,
    set_section_1_and_2_touched,
  } = isCompleteHelpers;
  return (
    <AccordionItem
      value="Sektion3"
      className={
        !advokatens_section_1_complete || !advokatens_section_2_complete
          ? "disabled"
          : ""
      }
    >
      <AccordionHeader onClick={set_section_1_and_2_touched}>
        <span>3.</span> <span>{texts.advokatens_section_3.title}</span>
      </AccordionHeader>
      <AccordionBody>
        <div className="vstack gap-5 pb-3">
          {(!advokatens_section_1_complete ||
            !advokatens_section_2_complete) && (
            <div className="callout callout-danger" role="alert">
              Du skal udfylde de foregående sektioner inden du kan gå videre.
            </div>
          )}
          <fieldset
            disabled={
              !advokatens_section_1_complete ||
              !advokatens_section_2_complete ||
              locked
            }
          >
            <FormRow padding="4">
              <FormRowLabel
                section="3.1"
                htmlFor={
                  texts.advokatens_section_3
                    .advokatens_section_3_1_pengeinstitut_indestaende_input
                    .inputId
                }
                column="col-9"
              >
                {
                  texts.advokatens_section_3
                    .advokatens_section_3_1_pengeinstitut_indestaende_input
                    .title
                }
                <IsRequired />
              </FormRowLabel>

              <FormRowInput column="col-3">
                <CurrencyField
                  name={
                    texts.advokatens_section_3
                      .advokatens_section_3_1_pengeinstitut_indestaende_input
                      .inputId
                  }
                  id={
                    texts.advokatens_section_3
                      .advokatens_section_3_1_pengeinstitut_indestaende_input
                      .inputId
                  }
                  amountName={
                    texts.advokatens_section_3
                      .advokatens_section_3_1_pengeinstitut_indestaende_input
                      .beloebId
                  }
                  touched={
                    touched.advokatens_section_3_1_pengeinstitut_indestaende_input
                  }
                  error={
                    errors.advokatens_section_3_1_pengeinstitut_indestaende_input
                  }
                  wrapper
                />
              </FormRowInput>
            </FormRow>
            <FormRow padding="4">
              <FormRowLabel
                section="3.2"
                htmlFor="bogholderietPrDato"
                column="col-9"
              >
                {
                  texts.advokatens_section_3
                    .advokatens_section_3_2_boghold_indestaende_beloeb.title
                }
              </FormRowLabel>
              <FormRowInput column="col-3">
                <FormTextGroup
                  formik={formikProps}
                  id="bogholderietPrDato"
                  value={(
                    values.advokatens_section_2_2_indestaaende_beloeb ?? 0
                  ).toLocaleString("da-DK", {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 2,
                    useGrouping: false,
                  })}
                  disabled
                />
              </FormRowInput>
            </FormRow>
            <FormRow padding="4">
              <FormRowLabel section="3.3" htmlFor="differencen2" column="col-9">
                {texts.advokatens_section_3.advokatens_section_3_3.title}
              </FormRowLabel>
              <FormRowInput column="col-3">
                <FormTextGroup
                  formik={formikProps}
                  id="differencen2"
                  disabled
                  value={new BigNumber(
                    values.advokatens_section_3_1_pengeinstitut_indestaende_beloeb ??
                      0,
                  )
                    .minus(
                      values.advokatens_section_2_2_indestaaende_beloeb ?? 0,
                    )
                    .toNumber()
                    .toLocaleString("da-DK", {
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                      useGrouping: false,
                    })}
                />
              </FormRowInput>
            </FormRow>
            <div className="vstack gap-4">
              <FormRow
                padding="0"
                className="border-0 row g-0 align-items-center"
              >
                <FormRowLabel
                  section="a."
                  htmlFor={
                    texts.advokatens_section_3.advokatens_section_3_3_a_input
                      .inputId
                  }
                  column="col-9"
                  className="ps-5"
                >
                  {
                    texts.advokatens_section_3.advokatens_section_3_3_a_input
                      .title
                  }
                </FormRowLabel>
                <FormRowInput column="col-3">
                  <CurrencyField
                    toggleMinus={
                      texts.advokatens_section_3
                        .advokatens_section_3_3_a_plus_minus.id
                    }
                    name={
                      texts.advokatens_section_3.advokatens_section_3_3_a_input
                        .inputId
                    }
                    id={
                      texts.advokatens_section_3.advokatens_section_3_3_a_input
                        .inputId
                    }
                    amountName={
                      texts.advokatens_section_3.advokatens_section_3_3_a_input
                        .beloebId
                    }
                    touched={
                      touched.advokatens_section_3_3
                        ?.advokatens_section_3_3_a_input
                    }
                    error={
                      errors.advokatens_section_3_3
                        ?.advokatens_section_3_3_a_input
                    }
                    wrapper
                  />
                </FormRowInput>
              </FormRow>

              <FormRow
                padding="0"
                className="border-0 row g-0 align-items-center"
              >
                <FormRowLabel
                  column="col-9"
                  section="b."
                  htmlFor={
                    texts.advokatens_section_3.advokatens_section_3_3_b_input
                      .inputId
                  }
                  className="ps-5"
                >
                  {
                    texts.advokatens_section_3.advokatens_section_3_3_b_input
                      .title
                  }
                </FormRowLabel>
                <FormRowInput column="col-3">
                  <CurrencyField
                    toggleMinus={
                      texts.advokatens_section_3
                        .advokatens_section_3_3_b_plus_minus.id
                    }
                    name={
                      texts.advokatens_section_3.advokatens_section_3_3_b_input
                        .inputId
                    }
                    id={
                      texts.advokatens_section_3.advokatens_section_3_3_b_input
                        .inputId
                    }
                    amountName={
                      texts.advokatens_section_3.advokatens_section_3_3_b_input
                        .beloebId
                    }
                    touched={
                      touched.advokatens_section_3_3
                        ?.advokatens_section_3_3_b_input
                    }
                    error={
                      errors.advokatens_section_3_3
                        ?.advokatens_section_3_3_b_input
                    }
                    wrapper
                  />
                </FormRowInput>
              </FormRow>
              <FormRow
                padding="0"
                className="border-0 row g-0 align-items-center"
              >
                <FormRowLabel
                  section="c."
                  column="col-9"
                  htmlFor={
                    texts.advokatens_section_3.advokatens_section_3_3_c_input
                      .inputId
                  }
                  className="ps-5"
                >
                  {
                    texts.advokatens_section_3.advokatens_section_3_3_c_input
                      .title
                  }
                </FormRowLabel>
                <FormRowInput column="col-3">
                  <CurrencyField
                    toggleMinus={
                      texts.advokatens_section_3
                        .advokatens_section_3_3_c_plus_minus.id
                    }
                    name={
                      texts.advokatens_section_3.advokatens_section_3_3_c_input
                        .inputId
                    }
                    id={
                      texts.advokatens_section_3.advokatens_section_3_3_c_input
                        .inputId
                    }
                    amountName={
                      texts.advokatens_section_3.advokatens_section_3_3_c_input
                        .beloebId
                    }
                    touched={
                      touched.advokatens_section_3_3
                        ?.advokatens_section_3_3_c_input
                    }
                    error={
                      errors.advokatens_section_3_3
                        ?.advokatens_section_3_3_c_input
                    }
                    wrapper
                  />
                </FormRowInput>
              </FormRow>
              <FormRow
                padding="0"
                className="border-0 row g-0 align-items-center"
              >
                <FormRowLabel
                  section="d."
                  column="col-9"
                  htmlFor={
                    texts.advokatens_section_3.advokatens_section_3_3_d_input
                      .inputId
                  }
                  className="ps-5"
                >
                  {
                    texts.advokatens_section_3.advokatens_section_3_3_d_input
                      .title
                  }
                </FormRowLabel>
                <FormRowInput column="col-3">
                  <CurrencyField
                    toggleMinus={
                      texts.advokatens_section_3
                        .advokatens_section_3_3_d_plus_minus.id
                    }
                    name={
                      texts.advokatens_section_3.advokatens_section_3_3_d_input
                        .inputId
                    }
                    id={
                      texts.advokatens_section_3.advokatens_section_3_3_d_input
                        .inputId
                    }
                    amountName={
                      texts.advokatens_section_3.advokatens_section_3_3_d_input
                        .beloebId
                    }
                    touched={
                      touched.advokatens_section_3_3
                        ?.advokatens_section_3_3_d_input
                    }
                    error={
                      errors.advokatens_section_3_3
                        ?.advokatens_section_3_3_d_input
                    }
                    wrapper
                  />
                </FormRowInput>
              </FormRow>
              <FormRow
                padding="0"
                className="border-0 row g-0 align-items-center"
              >
                <div className="col-9 d-flex align-items-center gap-3 ps-5">
                  <h5 className="fw-normal lh-base">e.</h5>
                  <label
                    className="form-label mb-0"
                    htmlFor={
                      texts.advokatens_section_3.advokatens_section_3_3_e_input
                        .inputId
                    }
                  >
                    {
                      texts.advokatens_section_3.advokatens_section_3_3_e_input
                        .title
                    }
                    :
                  </label>
                  <Field
                    autoComplete="off"
                    type="text"
                    className="form-control me-5"
                    name={
                      texts.advokatens_section_3
                        .advokatens_section_3_3_e_beskrivelse.id
                    }
                    placeholder="Beskrivelse"
                  />
                </div>
                <FormRowInput column="col-3">
                  <CurrencyField
                    toggleMinus={
                      texts.advokatens_section_3
                        .advokatens_section_3_3_e_plus_minus.id
                    }
                    name={
                      texts.advokatens_section_3.advokatens_section_3_3_e_input
                        .inputId
                    }
                    id={
                      texts.advokatens_section_3.advokatens_section_3_3_e_input
                        .inputId
                    }
                    amountName={
                      texts.advokatens_section_3.advokatens_section_3_3_e_input
                        .beloebId
                    }
                    touched={
                      touched.advokatens_section_3_3
                        ?.advokatens_section_3_3_e_input
                    }
                    error={
                      errors.advokatens_section_3_3
                        ?.advokatens_section_3_3_e_input
                    }
                    wrapper
                  />
                </FormRowInput>
              </FormRow>
              <FormRow>
                <div className="col-9">&nbsp;</div>
                <FormRowInput column="col-3">
                  <FormTextGroup
                    formik={formikProps}
                    id="andet1"
                    validationTouched={
                      typeof (errors?.advokatens_section_3_3 as any) ===
                      "string"
                    }
                    validationError={errors?.advokatens_section_3_3 as string}
                    value={getAfstemning(values)}
                    disabled
                  />
                </FormRowInput>
              </FormRow>
            </div>
          </fieldset>
        </div>
      </AccordionBody>
    </AccordionItem>
  );
};

export default AESection3;
